// Shared SVG blitter for the song lattice — draws a MapRenderModel's edges + nodes into
// an <svg>. Used by BOTH the full discovery map (build-time dot positions) and the
// standalone path view (runtime linear positions); the renderer is agnostic to where
// the x/y come from. Node/edge styling lives in CSS (map-node / map-edge classes).

import type { MapPanel, MapRenderModel, MapSelection } from "./discovery-map-model";
import { el } from "./dom";

// State as an icon, not a word (a fresh map read as a wall): ✓ mastered, ▶ unlocked, 🔒 locked.
const STATE_ICON: Record<string, string> = { mastered: "✓", unlocked: "▶", locked: "🔒" };

interface RailHandlers {
  onLoad(id: string): void;
  onShowPath(id: string): void;
}

/** The right-hand context rail — a node's songs, each with a load click + a 🧭 path
 *  action. Shared by the discovery map and the path view. */
export function buildRailPanel(panel: MapPanel, h: RailHandlers): HTMLElement {
  const aside = el("aside", { class: "map-rail" });
  const head = el("div", { class: "map-rail-head" });
  if (panel.num !== undefined) {
    head.append(el("span", { class: "map-rail-num", textContent: `#${panel.num}` }));
  }
  head.append(el("h3", { textContent: panel.heading }));
  if (panel.variant) {
    head.append(el("span", { class: "map-rail-variant", textContent: panel.variant }));
  }
  aside.append(head);
  if (panel.note) aside.append(el("div", { class: "map-rail-needs", textContent: panel.note }));
  if (panel.empty) {
    aside.append(el("div", { class: "map-rail-empty", textContent: panel.empty }));
    return aside;
  }
  for (const s of panel.rows) {
    const row = el("div", { class: `map-rail-row state-${s.state}${s.focused ? " focused" : ""}` });
    row.onclick = (ev) => {
      ev.stopPropagation();
      h.onLoad(s.id);
    };
    const pathBtn = el<HTMLButtonElement>("button", {
      class: "map-rail-path",
      textContent: "🧭",
      title: "Show the path to this song",
    });
    pathBtn.onclick = (ev) => {
      ev.stopPropagation();
      h.onShowPath(s.id);
    };
    row.append(
      el("span", { class: "map-rail-title", textContent: s.title }),
      el("span", {
        class: "map-rail-state",
        textContent: STATE_ICON[s.state] ?? s.state,
        title: s.state,
      }),
      pathBtn,
    );
    if (s.note) row.append(el("span", { class: "map-rail-lock", textContent: s.note }));
    aside.append(row);
  }
  return aside;
}

const SVG_NS = "http://www.w3.org/2000/svg";
export const NODE_W = 96;
export const NODE_H = 38;
export const MARGIN = 60;

export function svg<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number>,
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

// dot's spline is a start point followed by cubic-bézier triples → an SVG path.
function splinePath(pts: [number, number][]): string {
  const p0 = pts[0];
  if (!p0) return "";
  let d = `M ${p0[0]} ${p0[1]}`;
  for (let i = 1; i + 2 < pts.length; i += 3) {
    const c1 = pts[i];
    const c2 = pts[i + 1];
    const to = pts[i + 2];
    if (c1 && c2 && to) d += ` C ${c1[0]} ${c1[1]}, ${c2[0]} ${c2[1]}, ${to[0]} ${to[1]}`;
  }
  return d;
}

interface LatticeHandlers {
  onNode?: (id: string, hasSongs: boolean) => void;
  onEdge?: (sel: Extract<MapSelection, { kind: "edge" }>) => void;
}

/** Append the model's edge + node groups to `root`. Pure styling via CSS classes. */
export function drawLattice(
  root: SVGSVGElement,
  model: MapRenderModel,
  handlers: LatticeHandlers = {},
): void {
  for (const e of model.edges) {
    const d = e.points && e.points.length >= 4 ? splinePath(e.points) : null;
    const line = (cls: string): SVGElement =>
      d
        ? svg("path", { class: cls, d })
        : svg("line", { class: cls, x1: e.x1, y1: e.y1, x2: e.x2, y2: e.y2 });
    const g = svg("g", { class: `map-edge-group${e.focused ? " focused" : ""}` });
    if (e.adds.length > 0 && handlers.onEdge) {
      g.addEventListener("click", (ev) => {
        ev.stopPropagation();
        handlers.onEdge?.({
          kind: "edge",
          from: e.from,
          to: e.to,
          adds: e.adds,
          label: e.label,
          rowIndex: 0,
        });
      });
      g.append(line("map-edge-hit")); // wide transparent click target
    }
    g.append(line(`map-edge kind-${e.kind}`));
    if (e.label) {
      const label = svg("text", { class: "map-edge-label", x: e.labelX, y: e.labelY });
      label.textContent = e.label;
      g.append(label);
    }
    root.append(g);
  }
  for (const n of model.nodes) {
    const g = svg("g", { class: `map-node state-${n.state}${n.focused ? " focused" : ""}` });
    if (handlers.onNode) {
      g.addEventListener("click", (ev) => {
        ev.stopPropagation();
        handlers.onNode?.(n.id, n.songCount > 0);
      });
    }
    g.append(
      svg("rect", {
        x: n.x - NODE_W / 2,
        y: n.y - NODE_H / 2,
        width: NODE_W,
        height: NODE_H,
        rx: 7,
      }),
    );
    const fo = svg("foreignObject", {
      x: n.x - NODE_W / 2,
      y: n.y - NODE_H / 2,
      width: NODE_W,
      height: NODE_H,
    });
    const labelBox = el("div", { class: "map-node-label" });
    labelBox.append(el("span", { textContent: n.label }));
    fo.append(labelBox);
    g.append(fo);
    if (n.songCount > 0) {
      const count = svg("text", {
        class: "map-node-count",
        x: n.x + NODE_W / 2 - 4,
        y: n.y - NODE_H / 2 + 4,
      });
      count.textContent = String(n.songCount);
      g.append(count);
    }
    if (n.num !== undefined) {
      const num = svg("text", {
        class: "map-node-num",
        x: n.x - NODE_W / 2 + 4,
        y: n.y - NODE_H / 2 + 4,
      });
      num.textContent = `#${n.num}`;
      g.append(num);
    }
    root.append(g);
  }
}
